// VERA-246: interactive canvas for placing a custom logo directly on the tag.
// The buyer drags to position, drags the corner handle to resize, and uses the
// slider to rotate — capturing an exact transform (x/y/scale/rotation as % of the
// canvas) so production renders from intent, no design round-trip. Canvas shape +
// aspect come from the product's custom.width/height/shape metafields.

import React, { useCallback, useRef } from 'react'

import { alpha, Box, Slider, Stack, Typography, useTheme } from '@mui/material'
import { RotateCwIcon } from 'lucide-react'

export interface LogoPlacement {
  /** Logo centre as a % of canvas width/height (0–100). */
  xPct: number
  yPct: number
  /** Logo width as a % of canvas width (0–100). */
  scalePct: number
  /** Rotation in degrees (−180…180). */
  rotation: number
  /** Source pixel dimensions — kept for print pre-flight + production render. */
  naturalWidth: number
  naturalHeight: number
}

interface TagCanvasEditorProps {
  shape: 'round' | 'rect'
  widthMm: number
  heightMm: number
  tagColor: string
  /** Tag surface finish — 'gloss' adds a reflective sheen to the preview. */
  finish?: 'matte' | 'gloss'
  /** Logo print finish — 'gloss' adds a subtle sheen over the logo. */
  logoFinish?: 'matte' | 'gloss'
  /** VRG-156 — Woven "Embroidered" finish: render the logo as raised stitched thread
      (emboss + fine stitch texture) rather than a flat print. */
  embroidered?: boolean
  logoDataUrl: string
  placement: LogoPlacement
  onChange: (p: LogoPlacement) => void
}

const SAFE_INSET_PCT = 6 // matches logoPreflight SAFE_INSET (6%)
const MIN_SCALE = 4
const MAX_SCALE = 100

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export const TagCanvasEditor = ({
  shape,
  widthMm,
  heightMm,
  tagColor,
  finish = 'matte',
  logoFinish = 'matte',
  embroidered = false,
  logoDataUrl,
  placement,
  onChange,
}: TagCanvasEditorProps) => {
  const theme = useTheme()
  const canvasRef = useRef<HTMLDivElement | null>(null)
  // Live placement ref so pointer handlers always read the latest without re-binding.
  const placementRef = useRef(placement)
  placementRef.current = placement

  const aspect = widthMm > 0 && heightMm > 0 ? widthMm / heightMm : 1
  const isRound = shape === 'round'

  // ── Pointer interactions (drag to move, corner handle to resize) ──
  const startDrag = useCallback(
    (mode: 'move' | 'resize') => (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const canvas = canvasRef.current
      if (!canvas) return

      const onMove = (ev: PointerEvent) => {
        const rect = canvas.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        const px = ((ev.clientX - rect.left) / rect.width) * 100
        const py = ((ev.clientY - rect.top) / rect.height) * 100
        const p = placementRef.current
        if (mode === 'move') {
          onChange({ ...p, xPct: clamp(px, 0, 100), yPct: clamp(py, 0, 100) })
        } else {
          // Resize: logo half-width = horizontal distance from centre to pointer.
          const halfWidthPct = Math.abs(px - p.xPct)
          onChange({ ...p, scalePct: clamp(halfWidthPct * 2, MIN_SCALE, MAX_SCALE) })
        }
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [onChange],
  )

  const safeInset = isRound ? `${SAFE_INSET_PCT}%` : `${SAFE_INSET_PCT}%`

  return (
    <Stack spacing={3}>
      {/* Canvas */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 3,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary[500], 0.06),
        }}
      >
        <Box
          ref={canvasRef}
          sx={{
            position: 'relative',
            width: '70%',
            maxWidth: 260,
            aspectRatio: `${aspect}`,
            backgroundColor: tagColor,
            borderRadius: isRound ? '50%' : 2,
            border: `1px solid ${theme.palette.primary[200]}`,
            overflow: 'hidden',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          {/* Safe-area guide */}
          <Box
            sx={{
              position: 'absolute',
              inset: safeInset,
              borderRadius: isRound ? '50%' : 1,
              border: `1px dashed ${alpha(theme.palette.common.white, 0.4)}`,
              pointerEvents: 'none',
            }}
          />

          {/* Logo (draggable) */}
          <Box
            onPointerDown={startDrag('move')}
            sx={{
              position: 'absolute',
              left: `${placement.xPct}%`,
              top: `${placement.yPct}%`,
              width: `${placement.scalePct}%`,
              transform: `translate(-50%, -50%) rotate(${placement.rotation}deg)`,
              cursor: 'move',
              touchAction: 'none',
            }}
          >
            <Box
              component="img"
              src={logoDataUrl}
              alt="logo"
              draggable={false}
              sx={{
                width: '100%',
                display: 'block',
                pointerEvents: 'none',
                // Embroidered: emboss the logo so it reads as raised stitched thread —
                // a light top-left highlight + a dark bottom-right shadow, plus a touch of
                // saturation so the thread keeps the logo's TRUE colour (not greyed) (VRG-156).
                filter: embroidered
                  ? 'drop-shadow(0.9px 0.9px 0.4px rgba(0,0,0,0.5)) drop-shadow(-0.7px -0.7px 0.4px rgba(255,255,255,0.55)) saturate(1.35)'
                  : undefined,
              }}
            />
            {/* Embroidered stitch texture — raised satin-stitch thread masked to the logo
                pixels so only the logo (not the canvas) looks stitched (VRG-156). Two
                soft-light layers keep the logo's TRUE colour (soft-light modulates lightness,
                not hue) while reading clearly as thread: (1) defined diagonal satin rows with
                a bright ridge and a dark valley, (2) a finer perpendicular pass = the
                individual stitches crossing them. */}
            {embroidered && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'repeating-linear-gradient(45deg, rgba(255,255,255,0.85) 0px, rgba(255,255,255,0.85) 1px, rgba(0,0,0,0.55) 1.4px, rgba(0,0,0,0.55) 2.8px), ' +
                    'repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 0.8px, rgba(0,0,0,0.3) 0.8px, rgba(0,0,0,0.3) 2.2px)',
                  // overlay reads clearly as raised cross-hatched thread (soft-light was too
                  // faint on dark tags); the logo's saturate(1.35) keeps its colour rich.
                  mixBlendMode: 'overlay',
                  opacity: 1,
                  WebkitMaskImage: `url("${logoDataUrl}")`,
                  maskImage: `url("${logoDataUrl}")`,
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* Logo gloss sheen (screen-blended so it only lifts the printed pixels) */}
            {logoFinish === 'gloss' && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.1) 35%, rgba(255,255,255,0) 60%)',
                  mixBlendMode: 'screen',
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* Resize handle (bottom-right) */}
            <Box
              onPointerDown={startDrag('resize')}
              sx={{
                position: 'absolute',
                right: -6,
                bottom: -6,
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: theme.palette.common.white,
                border: '1px solid rgba(15,23,42,0.25)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.45)',
                cursor: 'nwse-resize',
                touchAction: 'none',
              }}
            />
          </Box>

          {/* Tag gloss sheen — a reflective highlight over the whole surface. */}
          {finish === 'gloss' && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0) 55%)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Rotation */}
      <Stack direction="row" spacing={3} alignItems="center">
        <RotateCwIcon size={16} strokeWidth={1} style={{ flexShrink: 0, color: theme.palette.text.secondary }} />
        <Slider
          size="small"
          min={-180}
          max={180}
          value={placement.rotation}
          onChange={(_, v) => onChange({ ...placement, rotation: v as number })}
          // Theme text colour (dark on light mode, white on dark) — a white slider was
          // invisible on the light-mode modal.
          sx={{ color: theme.palette.text.primary }}
        />
        <Typography variant="body3" color="text.secondary" sx={{ width: 44, textAlign: 'right', flexShrink: 0 }}>
          {Math.round(placement.rotation)}°
        </Typography>
      </Stack>

      <Typography variant="body4" color="text.secondary">
        Drag the logo to position it, drag the corner to resize, and use the slider to rotate.
      </Typography>
    </Stack>
  )
}
